import { Image, Radio, Tooltip } from 'antd';
import { AxiosResponse } from 'axios';
import { useEffect, useMemo } from 'react';
import { Rate, ShippingAddress, StorageItem } from '../../../../apis/client-axios';
import { SvgArrowDownSelect } from '../../../../components/@svg/SvgArrowDownSelect';
import CustomCheckbox from '../../../../components/checkbox';
import CustomSelect from '../../../../components/select/CustomSelect';
import { CheckIcon } from '../@svg/MagnifyingGlassIcon/CheckIcon/CheckIcon';
import { TDataRequest } from '../request';

export const AUTO_CHEAPEST_CARRIER = '__AUTO_CHEAPEST__';

type TProps = {
  dataRequest: TDataRequest;
  setDataRequest: React.Dispatch<React.SetStateAction<TDataRequest>>;
  itemDetail: AxiosResponse<StorageItem, any>;
  modalEditAddress: {
    isOpen: boolean;
    isChange: boolean;
    data: Omit<ShippingAddress, 'AddressID' | 'AddressOwner' | 'CountryName'>;
  };
  carrier: Rate[];
  loadingCarrier: boolean;
  required?: boolean;
  hasError?: boolean;
  enableAutoCheapest?: boolean;
  isMultiShip?: boolean;
  itemCount?: number;
};

enum SignType {
  UNNECESSARY = '不要',
  STANDARD = '必要',
  ADULT = '大人のサインが必須',
}

const AUTO_CHEAPEST_LABEL = '最安を自動選択';
const AUTO_CHEAPEST_LABEL_MULTILINE = (
  <>
    最安を
    <br />
    自動選択
  </>
);
const MULTI_ITEM_CARRIER_TOOLTIP = `表示される送料は概算です。
実際の同梱サイズ・重量が確定した時点で、最終的な送料が決まります。`;
const AUTO_CHEAPEST_TOOLTIP = `実際の同梱サイズ・重量が確定した時点で、
UPS・USPS・FedExの中から最も安い配送方法が自動で選択されます。`;
const CarrierInfoIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      d="M10 17.5C14.1421 17.5 17.5 14.1421 17.5 10C17.5 5.85786 14.1421 2.5 10 2.5C5.85786 2.5 2.5 5.85786 2.5 10C2.5 14.1421 5.85786 17.5 10 17.5Z"
      stroke="#514B4D"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M10 6.66699H10.0083V6.67533H10V6.66699Z" stroke="#514B4D" strokeWidth="2" strokeLinejoin="round" />
    <path d="M10 10V13.3333" stroke="#514B4D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Carrier = ({
  dataRequest,
  setDataRequest,
  modalEditAddress,
  carrier,
  required = true,
  hasError = false,
  enableAutoCheapest = false,
}: TProps) => {
  const cheapestCarrier = useMemo(() => {
    if (!carrier?.length) return undefined;
    return [...carrier].sort((a, b) => Number(a?.amount || 0) - Number(b?.amount || 0))[0];
  }, [carrier]);

  useEffect(() => {
    if (!carrier?.length) return;

    if (enableAutoCheapest && dataRequest.carrier === AUTO_CHEAPEST_CARRIER && cheapestCarrier) {
      const nextCarrierPrice = +cheapestCarrier.amount;
      const nextShippingRate = cheapestCarrier.objectId;

      if (
        dataRequest.carrierPrice === nextCarrierPrice &&
        dataRequest.shippingPlan === AUTO_CHEAPEST_LABEL &&
        dataRequest.shippingRate === nextShippingRate &&
        dataRequest.isPickUPSOutSide === false
      ) {
        return;
      }

      setDataRequest((prev) => ({
        ...prev,
        carrier: AUTO_CHEAPEST_CARRIER,
        carrierPrice: nextCarrierPrice,
        shippingPlan: AUTO_CHEAPEST_LABEL,
        shippingRate: nextShippingRate,
        isPickUPSOutSide: false,
      }));
      return;
    }

    const matchedCarrier = carrier.find((item) => item?.provider === dataRequest.carrier);

    if (matchedCarrier) {
      const nextCarrierPrice = +matchedCarrier.amount;
      const nextShippingPlan = matchedCarrier.servicelevel['displayName'] ?? matchedCarrier.servicelevel['name'];
      const nextShippingRate = matchedCarrier.objectId;

      if (
        dataRequest.carrierPrice === nextCarrierPrice &&
        dataRequest.shippingPlan === nextShippingPlan &&
        dataRequest.shippingRate === nextShippingRate
      ) {
        return;
      }

      setDataRequest((prev) => ({
        ...prev,
        carrierPrice: nextCarrierPrice,
        shippingPlan: nextShippingPlan,
        shippingRate: nextShippingRate,
      }));
      return;
    }

    if (
      dataRequest.carrierPrice === 0 &&
      dataRequest.shippingPlan === '' &&
      dataRequest.shippingRate === '' &&
      dataRequest.isPickUPSOutSide === false
    ) {
      return;
    }

    setDataRequest((prev) => ({
      ...prev,
      carrierPrice: 0,
      shippingPlan: '',
      shippingRate: '',
      isPickUPSOutSide: false,
    }));
  }, [
    carrier,
    cheapestCarrier,
    dataRequest.carrier,
    dataRequest.carrierPrice,
    dataRequest.isPickUPSOutSide,
    dataRequest.shippingPlan,
    dataRequest.shippingRate,
    enableAutoCheapest,
    setDataRequest,
  ]);

  const mapOption = useMemo(
    () => [
      {
        value: 'UNNECESSARY',
        label: (
          <div className="option-sign">
            <span className="text">{SignType.UNNECESSARY}</span>
            {dataRequest.signatureOption === 'UNNECESSARY' && <CheckIcon />}
          </div>
        ),
      },
      {
        value: 'STANDARD',
        label: (
          <div className="option-sign">
            <span className="text">{SignType.STANDARD}</span>
            {dataRequest.signatureOption === 'STANDARD' && <CheckIcon />}
          </div>
        ),
      },
      {
        value: 'ADULT',
        label: (
          <div className="option-sign">
            <span className="text">{SignType.ADULT}</span>
            {dataRequest.signatureOption === 'ADULT' && <CheckIcon />}
          </div>
        ),
      },
    ],
    [dataRequest.signatureOption],
  );

  const isJP = modalEditAddress.data.Country === 'JP';

  useEffect(() => {
    if (modalEditAddress.data.Country === 'JP') {
      setDataRequest((prev) => ({
        ...prev,
        category: prev.category || 'MERCHANDISE',
      }));
    } else {
      setDataRequest((prev) => ({
        ...prev,
        category: undefined,
      }));
    }
  }, [modalEditAddress.data.Country]);

  return (
    <div
      className={`box box-carrier ${hasError ? 'box-carrier--error' : ''} ${enableAutoCheapest ? 'box-carrier--auto-cheapest' : 'box-carrier--single-item'}`}
      style={hasError ? { border: '1px solid #ff4d4f' } : undefined}
    >
      <div className="title">
        <h3>配送会社</h3>
        {required && <span className="title-required">*</span>}
      </div>
      <div className="list-info content-partitions">
        <Radio.Group value={dataRequest.carrier}>
          <div className="carrier">
            {carrier
              ? carrier
                  .filter((v: Rate) => (dataRequest.confirm ? v?.provider === dataRequest.carrier : v))
                  .map((item, index) => (
                    <label
                      className={`carrier-item ${dataRequest.carrier === item.provider ? 'active' : ''} ${dataRequest.confirm ? 'disabled' : ''}`}
                      key={`${item.provider}-${item.objectId || index}`}
                    >
                      {enableAutoCheapest && (
                        <Tooltip
                          title={<span style={{ whiteSpace: 'pre-line' }}>{MULTI_ITEM_CARRIER_TOOLTIP}</span>}
                          placement="top"
                          overlayClassName="ship-buyer-carrier-tooltip"
                          overlayInnerStyle={{ width: 320, maxWidth: 320 }}
                        >
                          <span
                            className="carrier-item-information"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                          >
                            <CarrierInfoIcon />
                          </span>
                        </Tooltip>
                      )}
                      <Radio
                        disabled={dataRequest.confirm}
                        value={item.provider}
                        id={item.provider}
                        onChange={() => {
                          const isUPSOutsideUS = item.provider === 'UPS' && modalEditAddress.data.Country !== 'US';

                          setDataRequest({
                            ...dataRequest,
                            carrier: item.provider,
                            carrierPrice: +item.amount,
                            shippingPlan: item.servicelevel['displayName'] ?? item.servicelevel['name'],
                            shippingRate: item.objectId,
                            isPickUPSOutSide: isUPSOutsideUS,
                          });
                        }}
                      />

                      <div className="d-flex align-items-center chip-carrier">
                        <Image
                          preview={false}
                          width={64}
                          src={`/assets/icons/carrier/${item.provider?.toLowerCase()}.png`}
                        />
                        <div>
                          <span className="name">{item.provider}</span>
                          <div>
                            <span className="price">${Number(item.amount || 0).toFixed(2)}</span>
                          </div>
                          <div className="font-base color-sub-title font-weight-500 font-size-12 m-t-4 text-overflow-130 name-provider text-2-line">
                            {(item.servicelevel['displayName'] ?? item.servicelevel['name'])?.replace(/®/g, '').trim()}
                          </div>
                          <div className="estimated-day">お届け日数：{item.estimatedDays}日</div>
                        </div>
                      </div>
                    </label>
                  ))
              : null}
            {!dataRequest.confirm && enableAutoCheapest && cheapestCarrier && (
              <label
                className={`carrier-item carrier-item--auto ${dataRequest.carrier === AUTO_CHEAPEST_CARRIER ? 'active' : ''}`}
                key={AUTO_CHEAPEST_CARRIER}
              >
                <Tooltip
                  title={<span style={{ whiteSpace: 'pre-line' }}>{AUTO_CHEAPEST_TOOLTIP}</span>}
                  placement="top"
                  overlayClassName="ship-buyer-carrier-tooltip"
                  overlayInnerStyle={{ width: 320, maxWidth: 320 }}
                >
                  <span
                    className="carrier-item-information"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                  >
                    <CarrierInfoIcon />
                  </span>
                </Tooltip>
                <Radio
                  value={AUTO_CHEAPEST_CARRIER}
                  id={AUTO_CHEAPEST_CARRIER}
                  onChange={() => {
                    setDataRequest({
                      ...dataRequest,
                      carrier: AUTO_CHEAPEST_CARRIER,
                      carrierPrice: +cheapestCarrier.amount,
                      shippingPlan: AUTO_CHEAPEST_LABEL,
                      shippingRate: cheapestCarrier.objectId,
                      isPickUPSOutSide: false,
                    });
                  }}
                />
                <div className="d-flex align-items-center chip-carrier chip-carrier--auto">
                  <Image preview={false} width={64} height={64} src="/assets/icons/carrier/shipping_carrier.png" />
                  <div>
                    <span className="name">{AUTO_CHEAPEST_LABEL_MULTILINE}</span>
                  </div>
                </div>
              </label>
            )}
          </div>
        </Radio.Group>
        {(dataRequest.confirm && dataRequest.isInsurance) || !dataRequest.confirm ? (
          <div className="insurance-signature">
            <div className="insurance">
              <div className="insurance_wp">
                {!dataRequest.confirm ? (
                  <CustomCheckbox
                    className="insurance_item_checkbox"
                    checked={dataRequest.isInsurance}
                    onChange={({ target: { checked } }) => {
                      setDataRequest((prev) => ({
                        ...prev,
                        isInsurance: checked,
                      }));
                    }}
                    disabled={dataRequest.confirm}
                  />
                ) : (
                  <div></div>
                )}
                <label>保険料</label>
                <span className="colon">:</span>
                <div className="insurance_item_price">${dataRequest.insurance}</div>
              </div>
            </div>
            <div className="sign-select">
              <div className="sign">
                <div className="sign_wp">
                  {!dataRequest.confirm ? (
                    <div className="sign_item_select">
                      <label>署名オプション</label>
                      <CustomSelect
                        useParentPopupContainer={false}
                        placement="topLeft"
                        dropdownAlign={{
                          points: ['bl', 'tl'],
                          offset: [0, -4],
                          overflow: {
                            adjustX: 0,
                            adjustY: 0,
                          },
                        }}
                        className="custom-select-sign"
                        popupClassName="custom-select-sign-dropdown"
                        suffixIcon={<SvgArrowDownSelect />}
                        options={mapOption}
                        value={dataRequest.signatureOption}
                        onChange={(value: string) => {
                          setDataRequest((prev) => ({
                            ...prev,
                            signatureOption: value,
                            isSignatureOption: value !== 'UNNECESSARY',
                          }));
                        }}
                      />
                    </div>
                  ) : (
                    <div></div>
                  )}
                </div>
              </div>
              {isJP && (
                <div className="sign">
                  <div className="sign_wp">
                    <div className="sign_item_select">
                      <label>配送種別</label>
                      <CustomSelect
                        useParentPopupContainer={false}
                        placement="topLeft"
                        className="custom-select-jp"
                        popupClassName="custom-select-jp-dropdown"
                        suffixIcon={<SvgArrowDownSelect />}
                        options={[
                          {
                            value: 'MERCHANDISE',
                            label: 'MERCHANDISE',
                          },
                          {
                            value: 'RETURN_MERCHANDISE',
                            label: 'RETURN_MERCHANDISE',
                          },
                        ]}
                        value={dataRequest.category}
                        onChange={(value: 'MERCHANDISE' | 'RETURN_MERCHANDISE') => {
                          setDataRequest((prev) => ({
                            ...prev,
                            category: value,
                          }));
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="content-partitions"></div>
        )}
      </div>
    </div>
  );
};

export default Carrier;
